import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControlLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { LoadingSpinner } from 'src/consumer/components/Loaders'
import { toast } from 'react-toastify'
import {
  getPolicyTemplates,
  upsertPolicy,
} from 'src/business/services/APIs/CompanyCredits/companyCreditsApi'

export default function WalletPolicy({ summary, onRefresh }) {
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [selectedTemplateId, setSelectedTemplateId] = useState('')
  const [policy, setPolicy] = useState({
    policyType: 'monthlyAndOverall',
    monthlyUserCreditCap: 30,
    overallUserCreditCap: 200,
    allowNominatedRelatives: true,
    maxNominatedRelativesPerEmployee: 3,
    maxWhitelistedContactsPerRelative: 3,
    enforceCompanyEmailDomainForEmployees: false,
    relativesBypassDomainPolicy: true,
    allowedEmailDomains: [],
    allowedBookingClinicBrandIds: [],
    allowedBookingClinicIds: [],
    defaultBookingClinicBrandId: '',
    defaultBookingClinicId: '',
  })

  const existingPolicy = summary?.creditUsagePolicy || {}

  useEffect(() => {
    let cancelled = false
    getPolicyTemplates()
      .then((res) => {
        if (!cancelled && res?.status === 200) {
          const list = Array.isArray(res?.data) ? res.data : res?.data?.content ?? []
          setTemplates(Array.isArray(list) ? list : [])
        }
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    if (existingPolicy.policyType) {
      setPolicy((prev) => ({
        ...prev,
        policyType: existingPolicy.policyType || prev.policyType,
        monthlyUserCreditCap: existingPolicy.monthlyUserCreditCap ?? prev.monthlyUserCreditCap,
        overallUserCreditCap: existingPolicy.overallUserCreditCap ?? prev.overallUserCreditCap,
        allowNominatedRelatives: existingPolicy.allowNominatedRelatives ?? prev.allowNominatedRelatives,
        maxNominatedRelativesPerEmployee: existingPolicy.maxNominatedRelativesPerEmployee ?? prev.maxNominatedRelativesPerEmployee,
        maxWhitelistedContactsPerRelative: existingPolicy.maxWhitelistedContactsPerRelative ?? prev.maxWhitelistedContactsPerRelative,
        enforceCompanyEmailDomainForEmployees: existingPolicy.enforceCompanyEmailDomainForEmployees ?? prev.enforceCompanyEmailDomainForEmployees,
        relativesBypassDomainPolicy: existingPolicy.relativesBypassDomainPolicy ?? prev.relativesBypassDomainPolicy,
      }))
    }
  }, [existingPolicy.policyType, existingPolicy.monthlyUserCreditCap, existingPolicy.overallUserCreditCap])

  const applyTemplate = (t) => {
    if (!t?.policy) return
    setPolicy((prev) => ({ ...prev, ...t.policy }))
    setSelectedTemplateId(t.id || '')
  }

  const handleSave = async () => {
    if (policy.monthlyUserCreditCap < 0 || policy.overallUserCreditCap < 0) {
      toast.error('Caps cannot be negative')
      return
    }
    setSaving(true)
    try {
      const res = await upsertPolicy({
        currencyCode: summary?.currency || 'INR',
        policy,
      })
      if (res?.status === 200) {
        toast.success('Policy updated')
        onRefresh?.()
      } else {
        toast.error(res?.data?.message || 'Failed to update policy')
      }
    } catch (e) {
      toast.error('Failed to update policy')
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <LoadingSpinner bgColor="#F5F5F599" dark />

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {templates.length > 0 && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle2" gutterBottom>Apply a template</Typography>
            <Select
              size="small"
              value={selectedTemplateId}
              onChange={(e) => {
                const id = e.target.value
                setSelectedTemplateId(id)
                const t = templates.find((x) => x.id === id)
                if (t) applyTemplate(t)
              }}
              displayEmpty
              sx={{ minWidth: 260 }}
            >
              <MenuItem value="">Select template…</MenuItem>
              {templates.map((t) => (
                <MenuItem key={t.id || t.policyType} value={t.id || ''}>{t.id || t.policyType}</MenuItem>
              ))}
            </Select>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="subtitle1" fontWeight="600">Policy settings</Typography>
          <Select
            size="small"
            value={policy.policyType}
            onChange={(e) => setPolicy((p) => ({ ...p, policyType: e.target.value }))}
            label="Policy type"
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="unlimited">Unlimited</MenuItem>
            <MenuItem value="monthly">Monthly</MenuItem>
            <MenuItem value="overall">Overall</MenuItem>
            <MenuItem value="monthlyAndOverall">Monthly & overall</MenuItem>
          </Select>
          {(policy.policyType === 'monthly' || policy.policyType === 'monthlyAndOverall') && (
            <TextField
              size="small"
              type="number"
              label="Monthly user credit cap"
              value={policy.monthlyUserCreditCap}
              onChange={(e) => setPolicy((p) => ({ ...p, monthlyUserCreditCap: Math.max(0, parseInt(e.target.value, 10) || 0) }))}
              inputProps={{ min: 0 }}
            />
          )}
          {(policy.policyType === 'overall' || policy.policyType === 'monthlyAndOverall') && (
            <TextField
              size="small"
              type="number"
              label="Overall user credit cap"
              value={policy.overallUserCreditCap}
              onChange={(e) => setPolicy((p) => ({ ...p, overallUserCreditCap: Math.max(0, parseInt(e.target.value, 10) || 0) }))}
              inputProps={{ min: 0 }}
            />
          )}
          <FormControlLabel
            control={
              <Switch
                checked={!!policy.allowNominatedRelatives}
                onChange={(e) => setPolicy((p) => ({ ...p, allowNominatedRelatives: e.target.checked }))}
              />
            }
            label="Allow nominated relatives"
          />
          {policy.allowNominatedRelatives && (
            <>
              <TextField
                size="small"
                type="number"
                label="Max relatives per employee"
                value={policy.maxNominatedRelativesPerEmployee}
                onChange={(e) => setPolicy((p) => ({ ...p, maxNominatedRelativesPerEmployee: Math.max(0, parseInt(e.target.value, 10) || 0) }))}
                inputProps={{ min: 0 }}
              />
              <TextField
                size="small"
                type="number"
                label="Max whitelisted contacts per relative"
                value={policy.maxWhitelistedContactsPerRelative}
                onChange={(e) => setPolicy((p) => ({ ...p, maxWhitelistedContactsPerRelative: Math.max(0, parseInt(e.target.value, 10) || 0) }))}
                inputProps={{ min: 0 }}
              />
            </>
          )}
          <FormControlLabel
            control={
              <Switch
                checked={!!policy.enforceCompanyEmailDomainForEmployees}
                onChange={(e) => setPolicy((p) => ({ ...p, enforceCompanyEmailDomainForEmployees: e.target.checked }))}
              />
            }
            label="Enforce company email domain for employees"
          />
          <Box sx={{ pt: 1 }}>
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save policy'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  )
}
